import { Link } from "@tanstack/react-router";
import { Instagram, Music2, MessageCircle } from "lucide-react";
import { useContent } from "@/lib/use-content";
import { DynamicIcon } from "@/lib/icon-map";
import { LOGO_URL } from "@/lib/content-defaults";
import { openCookiePreferences } from "@/lib/cookie-consent";
import { useHayArticulos } from "@/lib/use-articulos";

export function SiteFooter() {
  const hayArticulos = useHayArticulos();
  const channels = useContent<{ items: { icon: string; k: string; v: string; href: string }[] }>("contacto_channels");
  const find = (k: string) => channels.items.find((c) => c.k === k);

  return (
    <footer className="border-t border-[var(--cp-line)] bg-black py-16 md:py-24">
      <div className="cp-container">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.5fr_1fr_1fr]">
          <div>
            <Link to="/" className="flex items-center gap-2">
              {/* alt="" a propósito: el nombre de la marca ya va como texto visible al lado (el
                  <span> de abajo) — mismo criterio que SiteNav.tsx (hallazgo real: alt repetido
                  con texto visible adyacente). */}
              <span className="grid h-11 w-12 place-items-center rounded-lg bg-white p-1.5">
                <img src={LOGO_URL} alt="" loading="lazy" decoding="async" className="h-full w-full object-contain" />
              </span>
              <span className="font-display text-lg tracking-[0.2em] text-white">CALISTENIA PROGRESIVA</span>
            </Link>
            <p className="mt-6 max-w-sm text-sm text-white/50 leading-relaxed">
              CP es una metodología de entrenamiento basada en progresiones estructuradas que permite desarrollar fuerza, control corporal y habilidades de forma segura y sostenible.
            </p>
            <div className="mt-8 flex gap-4">
              <SocialLink icon={<Instagram size={18}/>} href={find("Instagram")?.href || "#"} label="Instagram" />
              <SocialLink icon={<Music2 size={18}/>} href={find("TikTok")?.href || "#"} label="TikTok" />
              <SocialLink icon={<MessageCircle size={18}/>} href={find("WhatsApp")?.href || "#"} label="WhatsApp" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--cp-cyan)] mb-6">EXPLORAR</h3>
              <ul className="space-y-4">
                <FooterLink to="/">Inicio</FooterLink>
                <FooterLink to="/metodo">Método CP</FooterLink>
                <FooterLink to="/clases">Clases & Horarios</FooterLink>
                <FooterLink to="/nosotros">Nosotros</FooterLink>
              </ul>
            </div>
            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--cp-cyan)] mb-6">MÁS</h3>
              <ul className="space-y-4">
                <FooterLink to="/contacto">Contacto</FooterLink>
                <FooterLink to="/programa-kidstenia">Kidstenia</FooterLink>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--cp-cyan)] mb-6">CONTACTO</h3>
            <ul className="space-y-4">
              {["Ubicación", "Correo", "WhatsApp"].map((k) => {
                const c = find(k);
                if (!c) return null;
                return <ContactLine key={k} icon={c.icon} value={c.v} href={c.href} />;
              })}
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-[var(--cp-line)] flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
              © {new Date().getFullYear()} CALISTENIA PROGRESIVA · TODOS LOS DERECHOS RESERVADOS
            </p>
            <div className="flex items-center gap-4">
              {/* Único enlace a la sección de artículos (discreta): solo aparece si ya hay uno publicado. */}
              {hayArticulos && <Link to="/articulos" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[var(--cp-cyan)] transition">Artículos</Link>}
              <Link to="/privacidad" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[var(--cp-cyan)] transition">Privacidad</Link>
              <Link to="/terminos" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[var(--cp-cyan)] transition">Términos</Link>
              <button type="button" onClick={openCookiePreferences} className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-[var(--cp-cyan)] transition">Preferencias de cookies</button>
            </div>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">DISCIPLINA · CONSTANCIA · FE</p>
        </div>
      </div>
    </footer>
  );
}

function SocialLink({ icon, href, label }: { icon: React.ReactNode; href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="grid h-10 w-10 place-items-center border border-[var(--cp-line)] text-white/60 hover:text-[var(--cp-cyan)] hover:border-[var(--cp-cyan)] transition">
      {icon}
    </a>
  );
}

/** Una línea de contacto del footer (ver contacto_channels) — igual que ChannelCard en
 *  /contacto: si el link es interno (empieza con "/", ej. "/clases#ubicaciones" para
 *  "Ubicación"), navega con <Link> de la SPA en vez de recargar la página completa; el "#ancla"
 *  se separa del path porque el router hace el scroll a esa sección por su cuenta. */
function ContactLine({ icon, value, href }: { icon: string; value: string; href: string }) {
  const className = "flex items-start gap-3 text-sm text-white/60 hover:text-[var(--cp-cyan)] transition";
  const inner = <><DynamicIcon name={icon} size={16} className="text-[var(--cp-cyan)] shrink-0 mt-0.5" /> <span className="min-w-0 break-words">{value}</span></>;
  // Envuelto en <li>: este componente vive dentro de un <ul> (ver más arriba) — sin el <li>, el
  // <a>/<Link> quedaba como hijo directo del <ul>, markup inválido (un <ul> solo admite <li>,
  // <script> o <template> como hijos), hallazgo real de la auditoría de accesibilidad.
  if (href.startsWith("/")) {
    const [to, hash] = href.split("#");
    return <li><Link to={to} hash={hash} className={className}>{inner}</Link></li>;
  }
  return <li><a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={className}>{inner}</a></li>;
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="text-sm text-white/60 hover:text-white transition">{children}</Link>
    </li>
  );
}
